import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaApiService from '../../services/MangaApiService';
import MangaCardComponent from '../../components/mangacard/MangaCardComponent';
import MangaCatalogFilters, {
    EMPTY_FILTERS,
    ORDER_OPTIONS,
    TRANSLATION_OPTIONS,
    countActiveFilters,
} from './MangaCatalogFilters';
import { getMangaPref } from '../../utils/mangaPrefs';
import { CloseIcon } from './MangaIcons';
import MangaAccessNotice from './MangaAccessNotice';
import { unwrapList, getMangaErrorMessage } from './mangaStudioUtils';
import searchIcon from '../../assets/ic_search.svg';
import studioIcon from '../../assets/ic_settings.svg';
import filterIcon from '../../assets/filters.svg';

const STATUS_TABS = [
    { value: 'catalog', label: 'Каталог' },
    { value: 'READING', label: 'Читаю' },
    { value: 'PLAN_TO_READ', label: 'Запланировано' },
    { value: 'COMPLETED', label: 'Прочитано' },
    { value: 'DROPPED', label: 'Брошено' },
    { value: 'ON_HOLD', label: 'На паузе' },
];

// Same list as in the publishing rules; the server adds counts for genres in use.
const RULES_GENRES = ['Авангард', 'Гурман', 'Дзёсей', 'Драма', 'Комедия', 'Музыка', 'Повседневность', 'Приключения',
    'Романтика', 'Сверхъестественное', 'Сёдзё', 'Сёнен', 'Спорт', 'Сэйнэн', 'Тайна', 'Триллер', 'Ужасы',
    'Фантастика', 'Фэнтези', 'Экшен', 'Эротика', 'Этти'];


const PAGE_SIZE = 20;
const SCROLL_BUFFER_PX = 400;

const MangaPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('catalog');
    const [mangaList, setMangaList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [genres, setGenres] = useState(RULES_GENRES.map((name) => ({ name, count: 0 })));
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Sorting and status from the settings page are the starting point; the sheet can still change them.
    const [filters, setFilters] = useState(() => ({
        ...EMPTY_FILTERS,
        order: getMangaPref('catalogOrder'),
        translation: getMangaPref('catalogTranslation'),
    }));
    const { genres: selectedGenres, translation: translationFilter, order } = filters;
    const loadMoreRef = useRef(null);
    const pagesRequestedRef = useRef(new Set());
    const requestKeyRef = useRef('');
    const inflightCountRef = useRef(0);

    const isAllowed = MangaApiService.isAllowedDomain();
    const isRoleActive = (value) => value === true || value === 'true' || value === 1 || value === '1';
    const isModerator = isRoleActive(localStorage.getItem('mod'));
    const isEditor = isRoleActive(localStorage.getItem('editor'));
    const isStaff = isModerator || isEditor;

    useEffect(() => {
        document.title = 'animix - Манга';
    }, []);

    // Next page while the end of the grid is near. Unlike a scroll listener this also
    // keeps loading when the first page is too short to make the window scrollable.
    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel || statusFilter !== 'catalog' || !hasMore || loading || mangaList.length === 0) {
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setPage((prevPage) => prevPage + 1);
            }
        }, { rootMargin: `${SCROLL_BUFFER_PX}px 0px` });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loading, statusFilter, mangaList.length]);

    useEffect(() => {
        if (!isAllowed) {
            return;
        }
        MangaApiService.getMangaGenres()
            .then((list) => {
                const canonical = list
                    .filter((genre) => genre?.canonical)
                    .map((genre) => ({ name: genre.name, count: Number(genre.count) || 0 }))
                    .sort((left, right) => left.name.localeCompare(right.name, 'ru'));
                if (canonical.length > 0) {
                    setGenres(canonical);
                }
            })
            .catch(() => {});
    }, [isAllowed]);

    const filtersKey = `${selectedGenres.join(',')}|${translationFilter}|${order}`;
    const activeFilterCount = countActiveFilters(filters);
    const closeFilters = useCallback(() => setFiltersOpen(false), []);
    const appliedFilterChips = [
        ...(translationFilter !== 'all' ? [{
            key: 'translation',
            label: TRANSLATION_OPTIONS.find((option) => option.value === translationFilter)?.label,
            remove: () => setFilters((current) => ({ ...current, translation: 'all' })),
        }] : []),
        ...(order ? [{
            key: 'order',
            label: ORDER_OPTIONS.find((option) => option.value === order)?.label,
            remove: () => setFilters((current) => ({ ...current, order: '' })),
        }] : []),
        ...selectedGenres.map((genre) => ({
            key: `genre-${genre}`,
            label: genre,
            remove: () => setFilters((current) => ({ ...current, genres: current.genres.filter((item) => item !== genre) })),
        })),
    ];
    const isCatalog = statusFilter === 'catalog';

    const resetFilters = () => setFilters(EMPTY_FILTERS);

    const startLoading = () => {
        inflightCountRef.current += 1;
        setLoading(true);
    };

    const finishLoading = () => {
        inflightCountRef.current = Math.max(0, inflightCountRef.current - 1);
        if (inflightCountRef.current === 0) {
            setLoading(false);
        }
    };

    const mapMangaEntries = (entries) => (
        (entries || [])
            .map((entry) => entry.manga || entry.mangaEntity || entry.mangaItem || entry)
            .filter(Boolean)
    );

    const normalizeMangaPage = (response, requestedPage) => {
        if (!response) {
            return { items: [], last: true, page: requestedPage };
        }
        const payload = response?.data ?? response;
        if (Array.isArray(payload)) {
            return { items: payload, last: true, page: requestedPage };
        }
        const items = unwrapList(response);
        const metaCandidate = (payload
            && !Array.isArray(payload)
            && payload.data
            && !Array.isArray(payload.data)
            && typeof payload.data === 'object'
            && (
                payload.data.page !== undefined
                || payload.data.number !== undefined
                || payload.data.totalPages !== undefined
                || payload.data.total_pages !== undefined
                || payload.data.last !== undefined
            )) ? payload.data : payload;
        const pageValue = Number(metaCandidate?.number ?? metaCandidate?.page ?? requestedPage ?? 0);
        const totalPages = Number(metaCandidate?.totalPages ?? metaCandidate?.total_pages ?? 0);
        const last = metaCandidate?.last
            ?? (totalPages ? pageValue >= totalPages - 1 : items.length < PAGE_SIZE);
        return { items, last, page: pageValue };
    };

    const fetchMangas = async (pageNum = 0, requestKey = requestKeyRef.current) => {
        if (!isAllowed) {
            return;
        }
        if (statusFilter !== 'catalog' && !localStorage.getItem('sessionKey')) {
            if (requestKeyRef.current === requestKey) {
                setError('Войдите, чтобы смотреть свои списки манги.');
                setMangaList([]);
                setHasMore(false);
            }
            return;
        }

        const pageRequestKey = `${requestKey}|${pageNum}`;
        if (pagesRequestedRef.current.has(pageRequestKey)) {
            return;
        }
        pagesRequestedRef.current.add(pageRequestKey);

        startLoading();
        setError(null);
        try {
            if (statusFilter === 'catalog') {
                const response = await MangaApiService.searchMangas(searchQuery.trim(), {
                    page: pageNum,
                    size: PAGE_SIZE,
                    genres: selectedGenres,
                    completed: translationFilter === 'all' ? undefined : translationFilter === 'completed',
                    order,
                });
                const { items, last } = normalizeMangaPage(response, pageNum);
                if (requestKeyRef.current === requestKey) {
                    const mapped = mapMangaEntries(items);
                    setMangaList((prev) => (pageNum === 0 ? mapped : [...prev, ...mapped]));
                    setHasMore(!last);
                }
            } else {
                const response = await MangaApiService.getUserLists(statusFilter);
                if (requestKeyRef.current === requestKey) {
                    const mapped = mapMangaEntries(unwrapList(response));
                    setMangaList(mapped);
                    setHasMore(false);
                }
            }
        } catch (err) {
            if (requestKeyRef.current === requestKey) {
                setError(getMangaErrorMessage(err, 'Не удалось загрузить мангу.'));
                setMangaList([]);
                setHasMore(false);
            }
        } finally {
            finishLoading();
        }
    };

    useEffect(() => {
        if (!isAllowed) {
            return;
        }
        const trimmedQuery = searchQuery.trim();
        const nextRequestKey = `${statusFilter}|${trimmedQuery}|${filtersKey}`;
        requestKeyRef.current = nextRequestKey;
        pagesRequestedRef.current = new Set();
        setPage(0);
        setHasMore(true);
        setMangaList([]);
        const handle = setTimeout(() => {
            fetchMangas(0, nextRequestKey);
        }, 350);
        return () => clearTimeout(handle);
    }, [searchQuery, statusFilter, isAllowed, filtersKey]);

    useEffect(() => {
        if (!isAllowed || statusFilter !== 'catalog' || page === 0) {
            return;
        }
        fetchMangas(page, requestKeyRef.current);
    }, [page, statusFilter, isAllowed]);

    const isInitialLoading = loading && page === 0 && mangaList.length === 0;

    const filtersButton = isCatalog ? (
        <button
            type="button"
            className={`ui-btn ui-btn--secondary manga-filters-button ${activeFilterCount > 0 ? 'is-active' : ''}`}
            aria-haspopup="dialog"
            onClick={() => setFiltersOpen(true)}
        >
            <img src={filterIcon} alt="" aria-hidden="true" />
            <span>Фильтры</span>
            {activeFilterCount > 0 ? <span className="manga-filters-button__count">{activeFilterCount}</span> : null}
        </button>
    ) : null;

    return (
        <PageShell sidebar className="manga-catalog-page">
            {!isAllowed ? (
                <MangaAccessNotice />
            ) : (
                <>
                    <PageHeader
                        title="Манга"
                        actions={(
                            <Link className="ui-btn ui-btn--secondary" to="/manga/studio">
                                <img src={studioIcon} alt="" aria-hidden="true" />
                                Студия манги
                            </Link>
                        )}
                    >
                        <div className="manga-catalog-toolbar">
                            <div className="manga-search-row">
                                <label className="manga-search-field">
                                    <img src={searchIcon} alt="" aria-hidden="true" />
                                    <input
                                        type="search"
                                        className="manga-search"
                                        placeholder="Поиск по названию"
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                    />
                                </label>
                                {filtersButton}
                            </div>
                            <div className="ui-chips manga-status-chips" role="tablist" aria-label="Списки">
                                {STATUS_TABS.map((status) => (
                                    <button
                                        key={status.value}
                                        type="button"
                                        role="tab"
                                        aria-selected={statusFilter === status.value}
                                        className="ui-chip"
                                        onClick={() => setStatusFilter(status.value)}
                                    >
                                        {status.label}
                                    </button>
                                ))}
                            </div>
                            {isCatalog && appliedFilterChips.length > 0 ? (
                                <div className="ui-chips manga-applied-filters" aria-label="Применённые фильтры">
                                    {appliedFilterChips.map((chip) => (
                                        <button
                                            key={chip.key}
                                            type="button"
                                            className="ui-chip is-active"
                                            onClick={chip.remove}
                                            aria-label={`Убрать фильтр: ${chip.label}`}
                                        >
                                            {chip.label}
                                            <CloseIcon size={16} />
                                        </button>
                                    ))}
                                    <button type="button" className="ui-chip" onClick={resetFilters}>
                                        Сбросить
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </PageHeader>

                    <div className="manga-catalog-results">
                        {error ? <div className="manga-inline-warning">{error}</div> : null}
                        {isInitialLoading ? (
                            <div className="loader" />
                        ) : (
                            <div className="anime-container">
                                {mangaList.map((manga, index) => {
                                    const status = manga?.status || manga?.state || manga?.moderationStatus;
                                    const statusValue = status ? String(status).toUpperCase() : null;
                                    const visibilityLabel = isStaff && statusValue && statusValue !== 'PUBLISHED'
                                        ? 'Скрыт'
                                        : null;
                                    return (
                                        <MangaCardComponent
                                            key={manga.slug || manga.id || index}
                                            manga={manga}
                                            isInteractive
                                            visibilityLabel={visibilityLabel}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {!isInitialLoading && !loading && mangaList.length === 0 ? (
                            <div className="ui-empty">
                                <h2 className="ui-empty__title">
                                    {!isCatalog
                                        ? 'В этом списке пока пусто'
                                        : activeFilterCount > 0 || searchQuery.trim()
                                            ? 'Ничего не найдено'
                                            : 'Каталог пока пуст'}
                                </h2>
                                {isCatalog && (activeFilterCount > 0 || searchQuery.trim()) ? (
                                    <p className="ui-empty__text">Попробуйте изменить запрос или сбросить фильтры.</p>
                                ) : null}
                            </div>
                        ) : null}
                        <div ref={loadMoreRef} aria-hidden="true" />
                        {!isInitialLoading && loading && isCatalog ? <div className="loader" /> : null}
                    </div>
                </>
            )}
            <MangaCatalogFilters
                open={filtersOpen}
                value={filters}
                genres={genres}
                onClose={closeFilters}
                onApply={(next) => {
                    setFilters(next);
                    setFiltersOpen(false);
                }}
            />
        </PageShell>
    );
};

export default MangaPage;
